import React from "react";
import { Link } from "react-router-dom";
import { cleanAndSubstring } from "../utils/util";

const webinarBodyText = {
  id: {
    empty: "Belum ada webinar yang tersedia.",
    timeLabel: "Waktu",
    locationLabel: "Lokasi",
    readMore: "Lihat Detail",
    newest: "Terbaru",
    noTime: "Jadwal akan diperbarui",
    noLocation: "Lokasi akan diperbarui",
  },
  en: {
    empty: "No webinars available yet.",
    timeLabel: "Schedule",
    locationLabel: "Location",
    readMore: "View Details",
    newest: "Latest",
    noTime: "Schedule will be updated",
    noLocation: "Location will be updated",
  },
};

const monthNames = {
  id: [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "Mei",
    "Jun",
    "Jul",
    "Agu",
    "Sep",
    "Okt",
    "Nov",
    "Des",
  ],
  en: [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ],
};

const dayNames = {
  id: ["Min", "Sen", "Sel", "Rab", "Kam", "Jum", "Sab"],
  en: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
};

const stripHtml = (value) => (value || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();

const decodeHtml = (value) =>
  stripHtml(value)
    .replace(/&nbsp;/g, " ")
    .replace(/&#39;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/&amp;/g, "&");

const getDateParts = (value, lang) => {
  if (!value) {
    return { dayName: "", dayNumber: "", monthYear: "" };
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return { dayName: "", dayNumber: "", monthYear: "" };
  }

  const safeLang = monthNames[lang] ? lang : "id";

  return {
    dayName: dayNames[safeLang][date.getDay()],
    dayNumber: date.getDate(),
    monthYear: `${monthNames[safeLang][date.getMonth()]} ${date.getFullYear()}`,
  };
};

const getMetaValue = (description, labels) => {
  const text = decodeHtml(description);
  const escapedLabels = labels.map((label) => label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const pattern = new RegExp(`(?:${escapedLabels.join("|")})\\s*[:\\-]\\s*(.+?)(?=(?:${escapedLabels.join("|")}|Speakers?|Narasumber|Pembicara)\\s*[:\\-]|$)`, "i");
  const match = text.match(pattern);

  return match ? match[1].trim() : "";
};

const extractExcerpt = (description) => {
  const text = decodeHtml(description);
  const cleaned = text
    .replace(/^(date|day\/date|hari\/tanggal|time|waktu|venue|tempat)\s*:\s*.*$/gim, "")
    .replace(/^(speakers?|narasumber|pembicara)\s*:\s*.*$/gim, "")
    .trim();

  return cleanAndSubstring(cleaned || text, 260);
};

const webinarPagination = (total, page, perpage) => {
  const totalPages = Math.ceil((total || 0) / (perpage || 1));

  if (totalPages <= 1) {
    return null;
  }

  const links = [];

  for (let index = 1; index <= totalPages; index += 1) {
    links.push(
      <Link
        key={index}
        to={`/webinar/page/${index}`}
        className={parseInt(page, 10) === index ? "active" : ""}
      >
        {index}
      </Link>
    );
  }

  return (
    <div className="pagination">
      <Link to="/webinar/page/1">&laquo;</Link>
      {links}
      <Link to={`/webinar/page/${totalPages}`}>&raquo;</Link>
    </div>
  );
};

const WebinarListBody = ({ items, page, total, perpage }) => {
  const lang = window.lang || "id";
  const copy = webinarBodyText[lang] || webinarBodyText.id;

  return (
    <section className="webinar-list-section">
      <div className="container">
        {items && items.length ? (
          <div className="webinar-list">
            {items.map((item, index) => {
              const dateParts = getDateParts(item.DATE_EVENT || item.DATE_ADDED, lang);
              const schedule =
                getMetaValue(item.DESC_EVENT, ["Time", "Waktu"]) || copy.noTime;
              const location =
                getMetaValue(item.DESC_EVENT, ["Venue", "Location", "Tempat", "Lokasi"]) ||
                copy.noLocation;
              const image = item.FULL_PATH_IMAGE || item.FULL_PATH_IMAGE_THUM;
              const excerpt = extractExcerpt(item.DESC_EVENT || "");

              return (
                <article className="webinar-card" key={item.ID}>
                  <div className="webinar-date-panel">
                    <span className="webinar-date-day">{dateParts.dayName}</span>
                    <span className="webinar-date-number">{dateParts.dayNumber}</span>
                    <span className="webinar-date-month">{dateParts.monthYear}</span>
                  </div>
                  <div className="webinar-card-body">
                    <div className="webinar-meta-row">
                      {index === 0 ? (
                        <span className="webinar-status-pill">{copy.newest}</span>
                      ) : null}
                      <span className="webinar-schedule-text">{schedule}</span>
                    </div>
                    <Link to={`/webinar/${item.SLUG}`} className="webinar-card-title">
                      {item.NAME}
                    </Link>
                    <div className="webinar-detail-line">
                      <strong>{copy.timeLabel}:</strong> {schedule}
                    </div>
                    <div className="webinar-detail-line">
                      <strong>{copy.locationLabel}:</strong> {location}
                    </div>
                    <p className="webinar-card-excerpt">
                      {excerpt}
                      {excerpt.length >= 260 ? "..." : ""}
                    </p>
                    <Link to={`/webinar/${item.SLUG}`} className="publication-card-link">
                      {copy.readMore}
                    </Link>
                  </div>
                  <div className="webinar-image-panel">
                    {image ? (
                      <Link to={`/webinar/${item.SLUG}`}>
                        <img
                          src={image}
                          alt={item.NAME}
                          className="webinar-image"
                        />
                      </Link>
                    ) : null}
                  </div>
                </article>
              );
            })}
            <div className="webinar-pagination">
              {webinarPagination(total, page, perpage)}
            </div>
          </div>
        ) : (
          <div className="publication-empty-state">{copy.empty}</div>
        )}
      </div>
    </section>
  );
};

export default WebinarListBody;
